import { useMemo, useState } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { Color } from '@opendoor/tokens-studio/dist/bricks-next/types/Novo';
import { Stack, styled, useProps } from '@tamagui/core';
import { RadioGroup as TamaguiRadioGroup } from '@tamagui/radio-group';

import { useObservabilitySafe } from '../../../helpers/useObservabilitySafe';
import { isKeyOfObject } from '../../../helpers/utils';
import { Box } from '../../Box';
import { TrackableContainer } from '../../shared/TrackableContainer';
import { useInputId, useInputState } from '../shared/hooks';
import { TextInputFieldProps } from '../shared/TextInputField';
import { TextInput } from '../TextInput';
import { CheckboxRadioGroupError } from './shared/CheckboxRadioGroupError';
import { CheckboxRadioGroupLabel } from './shared/CheckboxRadioGroupLabel';
import { CheckboxRadioLabel, RightAlignedRadioLabel } from './shared/CheckboxRadioLabel';
import { getCustomRadioCheckboxRowStyling, useCheckBoxRadioState } from './shared/hooks';
import { SharedCheckboxRadioGroupProps, SharedSingleCheckboxRadioProps } from './shared/types';

const { radioCheckbox: radioTokens, formLayout: formLayoutTokens } = tokens;

export type RadioGroupBaseProps = SharedCheckboxRadioGroupProps & {
  /**
   * The default value for the radio group
   */
  defaultValue?: string;
  /**
   * Provides the parent component with a way to view the selected value of the radio group
   */
  onPress?: (value: string) => void;
};

type RadioGroupWithoutOtherOptionProps = RadioGroupBaseProps & {
  /**
   * Whether to show the "Other" option.
   */
  showOtherOption?: false;
};

type RadioGroupWithOtherOptionProps = RadioGroupBaseProps & {
  /**
   * Whether to show the "Other" option.
   */
  showOtherOption: true;
  /**
   * The title for the "Other" option. Required when showOtherOption is true.
   */
  otherTitle: string;
  /**
   * Provides the parent component with a way to view the entered value in the "Other" option.
   */
  onOtherTextChange?: (value: string) => void;
} & {
  /**
   * The onBlur event handler for the "Other" Text option.
   */
  onOtherTextBlur?: TextInputFieldProps['onBlur'];
};

export type RadioGroupProps = RadioGroupWithoutOtherOptionProps | RadioGroupWithOtherOptionProps;

/**
 * The Radio component allows users to select a single options from a predefined list.
 * Under the hood this is built with [Tamagui's radio group](https://tamagui.dev/docs/components/radio-group/1.3.0#radiogroup-1). This is a stateful component that keeps track of selected value.
 *
 *
 *
 * **Meaningful Properties:**
 * - onPress allows the parent to get the selected value
 * - To preselect a radio button, pass in the defaultValue prop
 * - Pass errorMessage or invalid if you want the error state to show up
 * - Pass disabled to disable the radio group. You can also disabled specific radio items by passing `disabled` in the options
 * - Passing required adds the aria-label: aria-required: true and pre-selects the first radio button
 * - The required prop is used to the required design (an asterisk next to the title) - radio group does not validate required for you. Please validate in the parent component's onSubmit function
 *
 * **Analytics:**
 * - Click Events: Radio click events will be tracked as ("radioClickEvent"), so `analyticsName` is required
 * - Impression Events: If you want to track impression events as ("radioboxViewEvent"), pass
 * `analyticsName` and `trackImpression` if you want to track the impression event
 *
 * **Accessibility:**
 *
 * Keyboard Interaction:
 * - The tab key moves keyboard focus to the checked raido button in the radiogroup - if none are checked, the first one is focused
 * - On space if the radio button with focus is unchecked, it becomes checked (not vice versa)
 * - The down arrow moves focus to the next radio button in the group. If focus is on the last radio button, it focus back to the first one
 * - The up arrow moves focus to the previous radio button in the group. If focus is on the first radio button, it focus back to the last radio button
 *
 * Radio Group:
 * - Role="radiogroup" attribute identifies the div element as a container for a group of radio buttons.
 * - Aria-labelledby attribute, points to the label text that describes the radio group
 * - When required is passed in - the aria-required attribute is set as true
 *
 * Radio:
 * - The role="radio" attribute identifies the element as an ARIA radio button
 * - The aria-checked attribute is used to indicate the state of the radio button
 */
export const RadioGroup = ({
  analyticsName,
  trackImpression,
  options,
  title,
  supportText,
  errorText,
  invalid,
  disabled,
  defaultValue,
  onPress,
  id,
  required,
  showOtherOption = false,
  ...props
}: RadioGroupProps) => {
  const { size = 'medium' } = useProps(props);

  const { trackJourneyEvent } = useObservabilitySafe();
  const groupLabelId = useInputId(id);
  const { state } = useInputState({
    errorText,
    invalid,
    disabled,
  });

  const isOtherDefaultValue =
    defaultValue && !options.some((option) => option.value === defaultValue);

  const [selectedValue, setSelectedValue] = useState<string>(defaultValue || '');
  const [otherValue, setOtherValue] = useState<string>(isOtherDefaultValue ? defaultValue : '');
  const handleCheckedChange = (value: string) => {
    setSelectedValue(value);
    // this onPress is optionally passed in from the parent component - allows it to get the selected value
    onPress && onPress(value);
    if (value !== 'other') {
      setOtherValue('');
      if (showOtherOption && 'onOtherTextChange' in props) {
        props.onOtherTextChange?.('');
      }
    }
    const data = { targetName: analyticsName, optionName: value };
    trackJourneyEvent('platform', 'radioClickEvent', data);
  };

  const handleOtherInputChange = (value: string) => {
    setOtherValue(value);
    if (showOtherOption && 'onOtherTextChange' in props) {
      props.onOtherTextChange?.(value);
    }
  };

  return (
    <TrackableContainer
      analyticsName={analyticsName}
      trackImpression={trackImpression}
      gap={radioTokens.padding.titleRowGap[size]}
      flexGrow={1}
      eventName="radioViewEvent"
    >
      <CheckboxRadioGroupLabel
        size={size}
        title={title}
        supportText={supportText}
        id={`${groupLabelId}-label`}
        state={state}
        required={required}
      />
      <TamaguiRadioGroup
        aria-labelledby={`${groupLabelId}-label`}
        defaultValue={defaultValue}
        name={groupLabelId}
        onValueChange={handleCheckedChange}
        required={required}
        gap={radioTokens.padding.rowGap[size]}
      >
        {options.map((option) => (
          <SingleRadioWithLabel
            key={option.value}
            selected={selectedValue === option.value}
            value={option.value}
            label={option.label}
            subtitle={option.subtitle}
            invalid={!!errorText || invalid}
            disabled={disabled || option.disabled}
            id={`${groupLabelId}-${option.value}`}
            rightAlignedText={option.rightAlignedText}
            {...props}
            size={size}
          />
        ))}
        {showOtherOption && (
          <OtherRadioWithTextbox
            selected={selectedValue === 'other'}
            size={size}
            onChangeText={handleOtherInputChange}
            onBlur={props.onBlur}
            onFocus={props.onFocus}
            onOtherTextBlur={'onOtherTextBlur' in props ? props.onOtherTextBlur : undefined}
            id={`${groupLabelId}-other`}
            title={'otherTitle' in props ? props.otherTitle : 'Other'}
            disabled={disabled || false}
            value={otherValue}
          />
        )}
      </TamaguiRadioGroup>
      <CheckboxRadioGroupError errorText={errorText} />
    </TrackableContainer>
  );
};

export type SingleRadioProps = SharedSingleCheckboxRadioProps & {
  /**
   * Indicates if the radio is selected
   */
  selected: boolean;
  /**
   * Optional right aligned value text
   */
  rightAlignedText?: string;
};
const SingleRadioWithLabel = ({
  selected,
  size,
  value,
  label,
  disabled,
  invalid,
  subtitle,
  id,
  onFocus,
  onBlur,
  rightAlignedText,
}: SingleRadioProps) => {
  const { rowState, buttonState, handleFocus, handleBlur } = useCheckBoxRadioState({
    checked: selected,
    disabled,
    onFocus,
    onBlur,
    invalid,
  });

  const { radioRowStyle, radioIndicatorStyle } = useMemo(
    () => getRadioStyling({ size, selected, rowState }),
    [size, selected, rowState],
  );

  return (
    <TamaguiRadioGroup.Item
      disabled={disabled}
      value={value}
      id={id}
      unstyled={true}
      display="flex"
      flexDirection="row"
      onFocus={handleFocus}
      onBlur={handleBlur}
      aria-invalid={invalid}
      {...radioRowStyle}
    >
      <CheckboxRadioLabel id={id} label={label} size={size} subtitle={subtitle} state={rowState} />
      {rightAlignedText && (
        <RightAlignedRadioLabel rightAlignedText={rightAlignedText} size={size} state={rowState} />
      )}
      <RadioIndicatorFrame size={size} state={buttonState}>
        <TamaguiRadioGroup.Indicator unstyled={true} {...radioIndicatorStyle} />
      </RadioIndicatorFrame>
    </TamaguiRadioGroup.Item>
  );
};

/**
 * Components used in Radio:
 *
 * - getRadioStyling - gets the row/indicator style for the radio
 * - RadioIndicatorFrame - styling for the circle that holds the radio indicator
 * - getRadioIndicatorStyling - gets the style for the radio indicator
 */
const getRadioStyling = ({
  size,
  rowState,
  selected,
}: {
  /**
   * size of the radio
   */
  size: SingleRadioProps['size'];
  /**
   * state of the radio adds "selected" (when not focused/invalid)
   */
  rowState: SingleRadioProps['state'];
  /**
   * If the radio is selected
   */
  selected: SingleRadioProps['selected'];
}) => {
  const radioRowStyle = getCustomRadioCheckboxRowStyling({
    size,
    state: rowState,
  });
  const mediumAndLarge = size === 'medium' || size === 'large';
  const radioIndicatorStyle = getRadioIndicatorStyling(selected, rowState, size);

  return {
    radioRowStyle: {
      ...radioRowStyle,
      // This is for the edge case for medium/large radio groups - these do not display the radiobutton
      // we still need to indicate that the row is selected when the state is invalid/disabled so we do it through the background color
      backgroundColor:
        mediumAndLarge &&
        selected &&
        isKeyOfObject(rowState, radioTokens.color.background.element.row.state.selected)
          ? radioTokens.color.background.element.row.state.selected[rowState]
          : ('$transparent' as Color),
    },
    radioIndicatorStyle,
  };
};
const RadioIndicatorFrame = styled(Stack, {
  borderWidth: radioTokens.borderWidth.default,
  borderColor: radioTokens.color.border.element.checkbox.default,
  backgroundColor: radioTokens.color.background.element.checkboxRadio.default,
  borderRadius: radioTokens.cornerRadius.element.radio.default,
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: 'auto',
  alignSelf: 'center',
  display: 'none',
  variants: {
    size: (size: SingleRadioProps['size']) => {
      const exteriorSize = size === 'xsmall' || size === 'small' ? 'small' : size;
      return {
        display: size === 'xsmall' ? 'flex' : 'none',
        width: radioTokens.size.element.radio.exterior[exteriorSize],
        height: radioTokens.size.element.radio.exterior[exteriorSize],
      };
    },

    state: (state: SingleRadioProps['state']) => {
      const borderColor = isKeyOfObject(state, radioTokens.color.border.element.checkbox.state)
        ? radioTokens.color.border.element.checkbox.state[state]
        : radioTokens.color.border.element.checkbox.default;

      // for radio buttons we want the background to stay the default background color when the state is invalid (for checkboxs its red)
      const backgroundColor =
        isKeyOfObject(state, radioTokens.color.background.element.checkboxRadio.state) &&
        state !== 'error'
          ? radioTokens.color.background.element.checkboxRadio.state[state]
          : '$transparent';

      return {
        borderColor,
        backgroundColor,
        borderWidth: radioTokens.borderWidth.default,
      };
    },
  } as const,
});

const getRadioIndicatorStyling = (
  selected: boolean,
  state: SingleRadioProps['state'],
  size: SingleRadioProps['size'],
) => {
  const interiorSize = size === 'xsmall' || size === 'small' ? 'small' : size;
  const heightAndWidth = isKeyOfObject(interiorSize, radioTokens.size.element.radio.interior)
    ? radioTokens.size.element.radio.interior[interiorSize]
    : 0;
  if (selected) {
    return {
      borderRadius: radioTokens.cornerRadius.element.radio.default,
      height: heightAndWidth,
      width: heightAndWidth,
      backgroundColor: isKeyOfObject(state, radioTokens.color.content.element.radioIcon.state)
        ? radioTokens.color.content.element.radioIcon.state[state]
        : radioTokens.color.content.element.radioIcon.default,
    };
  }
};

/**
 * Components used in other option radio
 */
type OtherRadioProps = {
  /**
   * If the other radio is selected
   */
  selected: boolean;
  /**
   * size of the other radio
   */
  size: SharedSingleCheckboxRadioProps['size'];
  /**
   * If the other radio is disabled
   */
  disabled: boolean;
  /**
   * id of the other radio
   */
  id: string;
  /**
   * title of the other radio's text input
   */
  title: string;
  /**
   * value of the other radio's text input
   */
  value: string;
  /**
   * The onBlur event handler for the other radio
   */
  onBlur?: SharedSingleCheckboxRadioProps['onBlur'];
  /**
   * The onBlur event handler for the other radio
   */
  onFocus?: SharedSingleCheckboxRadioProps['onFocus'];
  /**
   * The onBlur event handler for the "Other" Text option.
   */
  onOtherTextBlur?: TextInputFieldProps['onBlur'];
  /**
   * The onChangeText event handler for the "Other" Text option.
   */
  onChangeText: TextInputFieldProps['onChangeText'];
};

const OtherRadioWithTextbox = ({
  selected,
  size,
  disabled,
  id,
  title,
  value,
  onChangeText,
  onBlur,
  onFocus,
  onOtherTextBlur,
}: OtherRadioProps) => {
  return (
    <>
      <SingleRadioWithLabel
        label="Other"
        value="other"
        key="Other"
        selected={selected}
        size={size}
        id={id}
        disabled={disabled}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      {selected && (
        <Box
          animation="decelerateFast"
          animateOnly={['opacity', 'transform']}
          enterStyle={{ y: 40, opacity: 0 }}
          paddingTop={formLayoutTokens.padding.questions.betweenOtherAndTextinput[size]}
        >
          <TextInput
            size={size}
            onChangeText={onChangeText}
            disabled={disabled}
            title={title}
            required={true}
            inputMode="text"
            prefilledValue={value}
            onBlur={onOtherTextBlur}
          />
        </Box>
      )}
    </>
  );
};
